/**
 * O registro da área de administração (docs/spec/admin.md): quem fez o quê, em quem e quando, do
 * mais novo ao mais antigo. Tocar no nome da conta abre os detalhes dela (a conta apagada fica só
 * com o nome).
 */
import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { useLanguage, useT } from '../i18n/index.js';
import { ADMIN_LIMITS, type AdminLogEntry } from '../shared.js';
import { describeError, useApp } from '../store.js';
import { Button, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { EmptyState } from '../ui/EmptyState.js';
import { toast } from '../ui/Toaster.js';
import { isPluginAction, logDetailText } from './adminModel.js';
import { useDateText } from './AdminUsers.js';
import { usePlanName } from '../plans/usePlanName.js';

export function useDateTimeText(): (ms: number) => string {
  const language = useLanguage((s) => s.language);
  return (ms) => new Date(ms).toLocaleString(language, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function AdminLog({ version, onOpenUser }: { version: number; onOpenUser: (userId: string) => void }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const when = useDateTimeText();
  const date = useDateText();
  const name = usePlanName();
  const language = useLanguage((s) => s.language);
  const [entries, setEntries] = useState<AdminLogEntry[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  /** a lista da tela: muda a cada leitura do começo (o registro relido depois de uma ação) */
  const listRef = useRef(0);

  useEffect(() => {
    if (!token) return;
    let alive = true;
    listRef.current += 1;
    api.adminLog(token).then(
      (result) => {
        if (!alive) return;
        setEntries(result.entries);
        setHasMore(result.entries.length >= ADMIN_LIMITS.logPage);
        setError(null);
      },
      (failure: unknown) => {
        if (alive) setError(describeError(failure));
      },
    );
    return () => {
      alive = false;
    };
  }, [token, version]);

  const loadMore = async () => {
    const last = entries?.at(-1);
    if (!token || !last) return;
    const list = listRef.current;
    setLoadingMore(true);
    try {
      const result = await api.adminLog(token, { before: last.seq });
      // o registro foi relido enquanto a página carregava: ela viria repetida
      if (list !== listRef.current) return;
      setEntries((current) => [...(current ?? []), ...result.entries]);
      setHasMore(result.entries.length >= ADMIN_LIMITS.logPage);
    } catch (failure) {
      toast(describeError(failure), 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <section className="rd-adminLog" aria-label={t('admin.tab.log')}>
      {error ? (
        <p className="rd-adminUsers__error" role="alert">
          {error}
        </p>
      ) : null}
      {!entries && !error ? (
        <div className="rd-placeholder">
          <Spinner size={28} />
        </div>
      ) : null}
      {entries && !entries.length ? (
        <EmptyState icon="sheet" title={t('admin.log.empty')} />
      ) : null}
      {entries?.length ? (
        <div className="rd-adminTable rd-adminTable--log" role="table" aria-label={t('admin.tab.log')}>
          <div className="rd-adminTable__row rd-adminTable__row--head" role="row">
            {(['when', 'admin', 'action', 'target', 'detail'] as const).map((col) => (
              <span key={col} role="columnheader" className={`rd-adminTable__${col}`}>
                {t(`admin.log.col.${col}`)}
              </span>
            ))}
          </div>
          {entries.map((entry) => (
            <div key={entry.seq} className="rd-adminTable__row" role="row">
              <span role="cell" className="rd-adminTable__when">
                {when(entry.at)}
              </span>
              <span role="cell" className="rd-adminTable__admin">
                {entry.adminName}
              </span>
              <span role="cell" className={`rd-adminTable__action rd-adminTable__action--${entry.action}`}>
                {t(`admin.log.${entry.action}`)}
              </span>
              <span role="cell" className="rd-adminTable__target">
                {entry.targetId ? (
                  <button type="button" className="rd-adminLink" onClick={() => onOpenUser(entry.targetId!)}>
                    {entry.targetName}
                  </button>
                ) : entry.action === 'plan-config' ? (
                  // a edição de um plano não tem conta: o alvo é o plano
                  <span>{name(entry.targetName)}</span>
                ) : isPluginAction(entry.action) ? (
                  // nem a de um plugin oficial: o alvo é o plugin
                  <span>{entry.targetName}</span>
                ) : (
                  <span title={t('admin.log.gone')}>{entry.targetName}</span>
                )}
              </span>
              <span role="cell" className="rd-adminTable__detail">
                {logDetailText(t, entry, date, name, language)}
              </span>
            </div>
          ))}
        </div>
      ) : null}
      {hasMore ? (
        <Button className="rd-adminUsers__more" busy={loadingMore} onClick={() => void loadMore()}>
          {t('admin.loadMore')}
        </Button>
      ) : null}
    </section>
  );
}
